import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Route, HardHat, Building2, Truck,
  Phone, ArrowUpRight, Star, ChevronDown, MessageCircle,
} from "lucide-react";
import { API, SITE, waLink, PARISHES, SLOGAN, WORKMANSHIP, gallerySrc } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, Marquee, RoadDash } from "@/components/bits";
import BermudaMap from "@/components/BermudaMap";
import BeforeAfter from "@/components/BeforeAfter";

const HERO_IMG = "/work/excavation.jpg";
const ABOUT_IMG = "/work/parking-resurface.jpg";

const SERVICE_GROUPS = [
  { icon: Route, title: "Asphalt Services", to: "/asphalt-services", items: ["Asphalt Paving", "Asphalt Repairs", "Pothole Repairs", "Resurfacing"] },
  { icon: HardHat, title: "Construction Services", to: "/construction-services", items: ["General Construction", "Demolition", "Refurbishment"] },
  { icon: Building2, title: "Maintenance Services", to: "/maintenance", items: ["Property Maintenance", "Public Sector Maintenance", "Commercial Maintenance"] },
  { icon: Truck, title: "Logistics", to: "/construction-services", items: ["Trucking & Haulage"] },
];

const WHY = [
  { title: "Local", copy: "Proudly serving Bermuda." },
  { title: "Reliable", copy: "Professional, dependable service from start to finish." },
  { title: "Quality Work", copy: "Careful workmanship and attention to detail." },
  { title: "Simple Quotes", copy: "Tell us what you need and we'll help you find the right solution." },
];

const FAQS = [
  { q: "Do you provide free quotes?", a: "Contact JOELL with information about your project and we can discuss your requirements and quote process." },
  { q: "Do you handle small jobs?", a: "Yes. Contact us and tell us what you need done." },
  { q: "Do you provide construction services as well as asphalt services?", a: "Yes. JOELL provides asphalt services, construction, demolition, refurbishment, trucking and property maintenance." },
  { q: "Can I send pictures of the work I need completed?", a: "Yes. Customers can upload photos through the quote form or send photos through WhatsApp." },
  { q: "Do you work across Bermuda?", a: "Yes. JOELL serves customers throughout Bermuda." },
];

const MARQUEE_ITEMS = [
  "Asphalt Paving", "Pothole Repairs", "Demolition", "Trucking & Haulage",
  "Resurfacing", "Refurbishment", "Construction", "Island-Wide Service",
];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.85, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] } }),
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  const lines = ["Asphalt Services.", "Construction Services."];

  return (
    <section ref={ref} className="relative isolate flex min-h-[94vh] items-end overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${HERO_IMG})` }}
        className="absolute inset-0 -z-10 scale-110 bg-cover bg-center"
        aria-hidden="true"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-joell-black via-joell-black/70 to-joell-black/45" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 asphalt-grain opacity-70" aria-hidden="true" />

      <div className="container-x pb-14 pt-28 md:pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <img src="/logo.png" alt="JOELL — Asphalt Service & Maintenance" className="h-28 w-auto drop-shadow-2xl md:h-36" data-testid="hero-logo" />
          <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-white/80 sm:text-xs">
            Asphalt Service • Maintenance
          </p>
          <p className="mt-3 font-display text-sm font-semibold uppercase tracking-[0.18em] text-joell-yellow sm:text-base" data-testid="hero-slogan">
            &ldquo;{SLOGAN}&rdquo;
          </p>
        </motion.div>

        <h1 className="mt-8 font-display font-bold uppercase leading-[0.98] tracking-tight text-white text-[clamp(2.4rem,8.5vw,6.5rem)]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className="block">
                {line.replace(".", "")}
                <span className="text-joell-yellow">.</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
            Experienced asphalt and construction services across Bermuda. We provide professional paving,
            resurfacing, pothole remediation, repairs and general construction services, with a focus on quality
            workmanship, clean finishes and dependable service.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/request-quote" className="btn-accent !px-8 !py-4" data-testid="hero-get-quote-button">Get a Free Quote</Link>
            <a href={SITE.phoneHref} className="btn-outline !px-8 !py-4" data-testid="hero-call-button">
              <Phone size={15} /> Call {SITE.phone}
            </a>
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" data-testid="hero-supporting-text">
            Serving residential and commercial properties across Bermuda.
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" data-testid="hero-phones">
            Call or WhatsApp: <a href={SITE.phoneHref} className="text-joell-yellow">441-777-0584</a> · <a href={SITE.phone2Href} className="text-joell-yellow">441-535-0584</a>
          </p>
        </motion.div>
      </div>

      <div className="absolute inset-x-0 bottom-0">
        <RoadDash />
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="container-x py-20 md:py-28" data-testid="services-section">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHead
          eyebrow="What we do"
          title={<>Professional services built around your project</>}
          className="!mb-0"
        />
        <Reveal delay={0.1}>
          <Link to="/request-quote" className="btn-accent" data-testid="services-view-all-button">Request a Quote</Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICE_GROUPS.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.06}>
            <Link
              to={g.to}
              className="group flex h-full flex-col bg-joell-black p-7 transition-colors duration-300 hover:bg-joell-surface md:p-8"
              data-testid={`service-group-${i}`}
            >
              <div className="flex items-start justify-between">
                <g.icon size={28} strokeWidth={1.6} className="text-joell-yellow" />
                <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
              </div>
              <h3 className="mt-8 font-display text-xl font-bold uppercase tracking-wide text-white">{g.title}</h3>
              <ul className="mt-5 flex-1 space-y-2.5">
                {g.items.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-400">
                    <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                    {item}
                  </li>
                ))}
              </ul>
              <span className="mt-6 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-joell-yellow opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                Learn more <ArrowUpRight size={13} />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhyJoell() {
  return (
    <section className="border-y border-white/10 bg-joell-coal/50 py-20 md:py-28" data-testid="why-joell-section">
      <div className="container-x">
        <SectionHead eyebrow="Why choose JOELL" title="Why JOELL?" />
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08} className="border-l-2 border-joell-yellow/60 pl-6">
              <span className="font-mono text-xs text-joell-yellow">0{i + 1}</span>
              <h3 className="mt-3 font-display text-2xl font-bold uppercase text-white">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{w.copy}</p>
            </Reveal>
          ))}
        </div>
        <RoadDash className="mt-14" />
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="workmanship-strip">
          {WORKMANSHIP.map((w, i) => (
            <Reveal key={w} delay={i * 0.05}>
              <div className="flex items-center gap-3 border border-white/10 bg-joell-black px-4 py-4">
                <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{w}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkPreview() {
  const { data: items = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await axios.get(`${API}/gallery`)).data,
  });
  const shots = items.filter((i) => !i.is_before_after && !i.is_video).slice(0, 6);
  const baItem = items.find((i) => i.is_before_after);

  return (
    <section className="container-x py-20 md:py-28" data-testid="work-preview-section">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHead
          eyebrow="Portfolio"
          title="Our Work"
          sub="A look at completed projects showcasing our workmanship, attention to detail and range of services."
          className="!mb-0"
        />
        <Reveal delay={0.1}>
          <Link to="/our-work" className="btn-outline" data-testid="work-preview-view-all-button">Visit the Gallery</Link>
        </Reveal>
      </div>

      {shots.length === 0 && !baItem ? (
        <p className="mt-12 border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500">
          Project photos coming soon
        </p>
      ) : (
        <>
          {baItem && (
            <Reveal delay={0.05} className="mt-12">
              <div className="mx-auto max-w-3xl">
                <BeforeAfter
                  beforeSrc={gallerySrc(baItem, "before")}
                  afterSrc={gallerySrc(baItem, "after")}
                  title={baItem.title}
                />
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{baItem.title}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-joell-yellow">Before &amp; After — drag the handle</span>
                </div>
              </div>
            </Reveal>
          )}

          <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {shots.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.05}>
              <Link to="/our-work" className="group relative block aspect-[4/3] overflow-hidden border border-white/10" data-testid={`work-preview-item-${i}`}>
                <img
                  src={gallerySrc(item)}
                  alt={item.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-joell-black/85 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-4">
                  <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{item.title}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-joell-yellow">{item.category}</span>
                </div>
              </Link>
            </Reveal>
          ))}
          </div>
        </>
      )}
    </section>
  );
}

function QuickQuote() {
  const steps = [
    "Take photos of the area",
    "Tell us what you need",
    "Send your location",
    "JOELL reviews your request",
  ];
  return (
    <section className="border-y border-white/10 bg-joell-coal/50 py-20 md:py-28" data-testid="quick-quote-section">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2">
        <div>
          <SectionHead
            eyebrow="Quick quote"
            title="Get a faster quote"
            sub="Take a few photos of the area that needs work and send them to JOELL."
            className="!mb-8"
          />
          <Reveal delay={0.1}>
            <a
              href={waLink("Hi JOELL, I'd like a faster quote. I'm sending photos of the area that needs work.")}
              target="_blank"
              rel="noreferrer"
              className="btn-accent !px-8 !py-4"
              data-testid="quick-quote-whatsapp-button"
            >
              <MessageCircle size={16} /> Send Photos on WhatsApp
            </a>
          </Reveal>
        </div>
        <ol className="space-y-0">
          {steps.map((s, i) => (
            <Reveal key={s} delay={i * 0.08}>
              <li className="flex items-center gap-5 border-b border-white/10 py-5 last:border-b-0" data-testid={`quick-quote-step-${i + 1}`}>
                <span className="font-display text-3xl font-bold text-joell-yellow">0{i + 1}</span>
                <span className="flex-1 font-display text-lg font-semibold uppercase tracking-wide text-white sm:text-xl">{s}</span>
                <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rotate-45 border-t-2 border-r-2 border-joell-green/60" />
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section className="container-x py-20 md:py-28" data-testid="about-teaser-section">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-3 -top-3 h-full w-full border-2 border-joell-yellow" aria-hidden="true" />
            <img src={ABOUT_IMG} alt="Asphalt paving work" loading="lazy" className="relative w-full border border-white/10 object-cover aspect-[4/3]" />
          </div>
        </Reveal>
        <div>
          <SectionHead
            eyebrow="About JOELL"
            title="Built on experience. Driven by pride in the work."
            sub="Joell Asphalt Service & Maintenance Ltd. is a Bermuda-based company led by Eston E Joell, providing asphalt, construction and repair services across the island."
            className="!mb-6"
          />
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[
                ["18", "Years in operation"],
                ["30+", "Years industry experience"],
                ["R/C/G", "Residential, commercial & government"],
              ].map(([n, label]) => (
                <div key={label} className="border border-white/10 bg-joell-surface p-4" data-testid="about-teaser-stat">
                  <p className="font-display text-3xl font-bold text-joell-yellow">{n}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-outline mt-8" data-testid="about-teaser-link">More About JOELL</Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function QuoteCallout() {
  return (
    <section className="bg-joell-yellow py-20 text-joell-black md:py-24" data-testid="quote-callout-section">
      <div className="container-x text-center">
        <Reveal>
          <h2 className="font-display text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl">
            Have a job that needs doing?
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-semibold text-joell-black/80">Tell JOELL what you need.</p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/request-quote" className="btn-dark !border-2 !border-joell-black !px-8 !py-4 hover:!bg-joell-ink" data-testid="callout-quote-button">Request a Free Quote</Link>
            <a href={SITE.phoneHref} className="btn-outline !border-joell-black !px-8 !py-4 !text-joell-black hover:!border-joell-ink hover:!text-joell-yellow" data-testid="callout-call-button">
              <Phone size={15} /> Call {SITE.phone}
            </a>
            <a href={waLink("Hi JOELL, I have a job that needs doing.")} target="_blank" rel="noreferrer" className="btn-outline !border-joell-black !px-8 !py-4 !text-joell-black hover:!border-joell-ink hover:!text-joell-yellow" data-testid="callout-whatsapp-button">
              <MessageCircle size={15} /> WhatsApp JOELL
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonials() {
  const { data: items = [] } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => (await axios.get(`${API}/testimonials`)).data,
  });

  return (
    <section className="container-x py-20 md:py-28" data-testid="testimonials-section">
      <SectionHead eyebrow="Reviews" title="What our customers say" />
      {items.length === 0 ? (
        <Reveal>
          <p className="border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="testimonials-empty">
            Customer reviews will appear here soon
          </p>
        </Reveal>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.06}>
              <figure className="flex h-full flex-col border border-white/10 bg-joell-surface p-7" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1" aria-label={`${t.rating} out of 5 stars`}>
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} size={14} className="fill-joell-yellow text-joell-yellow" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">&ldquo;{t.text}&rdquo;</blockquote>
                <figcaption className="mt-6 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-white">
                  {t.name}{t.location && <span className="text-slate-500"> — {t.location}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}

function ServiceArea() {
  return (
    <section className="border-t border-white/10 bg-joell-coal/50 py-20 md:py-28" data-testid="service-area-section">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2">
        <div>
          <SectionHead
            eyebrow="Service area"
            title="Serving clients across Bermuda"
            sub="From residential properties to commercial and public-sector projects, Joell Asphalt Service & Maintenance Ltd. provides asphalt, construction and repair services across the island."
            className="!mb-8"
          />
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            {PARISHES.slice(0, 9).map((p, i) => (
              <Reveal key={p} delay={i * 0.04}>
                <span className="flex items-center gap-2.5 text-sm text-slate-300">
                  <span aria-hidden="true" className="inline-block h-2 w-2 rotate-45 border-t-2 border-r-2 border-joell-green" />
                  {p}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.1} className="border border-white/10 bg-joell-black p-6 sm:p-10">
          <BermudaMap />
        </Reveal>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="container-x py-20 md:py-28" data-testid="faq-section">
      <SectionHead eyebrow="FAQ" title="Common questions" />
      <div className="border-t border-white/10">
        {FAQS.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.04}>
            <div className="border-b border-white/10">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
                aria-expanded={open === i}
                data-testid={`faq-question-${i}`}
              >
                <span className="font-display text-lg font-semibold uppercase tracking-wide text-white sm:text-xl">{f.q}</span>
                <ChevronDown size={20} className={`shrink-0 text-joell-yellow transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <motion.p
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden pb-6 pr-10 text-sm leading-relaxed text-slate-400 sm:text-base"
                  data-testid={`faq-answer-${i}`}
                >
                  {f.a}
                </motion.p>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  usePageTitle("");
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Services />
      <WhyJoell />
      <WorkPreview />
      <QuickQuote />
      <AboutTeaser />
      <QuoteCallout />
      <Testimonials />
      <ServiceArea />
      <Faq />
    </>
  );
}
